/**
 * Itadakimasu FAQ behaviour: vanilla JS, no dependencies, loaded with `defer` by
 * sections/itk-faq.liquid (product page) and sections/itk-faq-page.liquid (/pages/faq).
 *
 *   1. Deep links: the <details class="acc--faq"> named by location.hash opens on load and on hashchange, and
 *      is scrolled into view (the global [id] scroll-margin keeps it below the header). Row ids are
 *      "faq-" + the entry handle without its own leading "faq-" (#faq-gummies-how-much-sugar-is-in-a-serving);
 *      the older doubled form (#faq-faq-…) still resolves. The details height animation is switched off for
 *      that one open, so the scroll lands on the final layout.
 *   2. Search (FAQ page): [data-itk-faq-input] filters questions + answers as you type (120 ms debounce).
 *      Non-matching rows and empty topics get [hidden], matches open, the count is announced in the
 *      role="status" line (no matches also adds .itk-faq-page--no-results, which hides the phone chip row),
 *      and clearing the field restores the page exactly as it was rendered.
 *   3. Scroll-spy (FAQ page): the topic link of the section being read gets aria-current="true"; in the
 *      phone chip row it is scrolled into view horizontally. Focus is never moved.
 *
 * Without JS every question and answer is still in the page (native details/summary) and the search
 * field stays hidden.
 */
(function () {
  'use strict';

  const ITK = (window.ITK = window.ITK || {});
  if (ITK.faqReady) return; // both FAQ sections include this file; bind once
  ITK.faqReady = true;

  const DEBOUNCE_MS = 120;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const chipQuery = window.matchMedia('(max-width: 989px)');

  /** Lower-case, accent-free, straight quotes, single spaces: "Don’t" and "dont'" compare sensibly. */
  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[‘’‛]/g, "'")
      .replace(/[“”]/g, '"')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function fill(template, count, query) {
    return String(template || '')
      .replace(/\[count\]/g, String(count))
      .replace(/\[q\]/g, () => query);
  }

  /* ------------------------------------------------------------------ 1. deep links */

  const ROW_PREFIX = 'faq-';
  const INSTANT_CLASS = 'itk-faq-instant';
  let instantFrame = 0;

  function rowFromHash() {
    const raw = window.location.hash.slice(1);
    if (!raw) return null;
    let id = raw;
    try {
      id = decodeURIComponent(raw);
    } catch (e) {
      /* keep the raw hash */
    }
    let target = document.getElementById(id);
    // links shared before the ids dropped the handle's own "faq-" looked like #faq-faq-gummies-…
    if (!target && id.indexOf(ROW_PREFIX + ROW_PREFIX) === 0) {
      target = document.getElementById(id.slice(ROW_PREFIX.length));
    }
    return target ? target.closest('details.acc--faq') : null;
  }

  /** Run fn with the details height transition off (html.itk-faq-instant), switched back on two frames later. */
  function withoutTransitions(fn) {
    const html = document.documentElement;
    cancelAnimationFrame(instantFrame);
    html.classList.add(INSTANT_CLASS);
    try {
      fn();
    } finally {
      instantFrame = requestAnimationFrame(() => {
        instantFrame = requestAnimationFrame(() => html.classList.remove(INSTANT_CLASS));
      });
    }
  }

  function openFromHash() {
    const row = rowFromHash();
    if (!row) return;
    // rows animate their height (::details-content); a reset search or the exclusive group closing other rows
    // would still be mid-animation when scrollIntoView measures, so do it all without transitions
    withoutTransitions(() => {
      // a row hidden by an active search must be visible before it can be shown
      const page = row.closest('[data-itk-faq-page]');
      if (page && page.itkFaq && row.closest('[hidden]')) page.itkFaq.reset();
      row.open = true; // on the product page this also closes the other rows of the name="faq-pdp" group
      // opening (and closing siblings) can move the row, so land on it again; CSS decides smooth vs instant
      row.scrollIntoView({ block: 'start' });
    });
  }

  /* ------------------------------------------------------------------ 2 + 3. FAQ page */

  function initPage(root) {
    if (root.itkFaq) return;

    const topics = Array.from(root.querySelectorAll('[data-itk-faq-topic]')).map((el) => {
      const link = el.id ? root.querySelector('[data-itk-faq-nav] a[href="#' + CSS.escape(el.id) + '"]') : null;
      const rows = Array.from(el.querySelectorAll('details.acc--faq')).map((row) => {
        const q = row.querySelector('.faq__q');
        const a = row.querySelector('.faq__a');
        return {
          el: row,
          open: row.open, // the rendered state, restored when the search is cleared
          text: normalize((q ? q.textContent : '') + ' ' + (a ? a.textContent : ''))
        };
      });
      return { el, link, item: link ? link.closest('li') : null, rows };
    });
    const rows = topics.reduce((all, topic) => all.concat(topic.rows), []);

    // scroll-spy state first: the search below may call requestSpy() straight away
    const header = document.querySelector('.header');
    const aside = root.querySelector('.itk-faq-page__aside');
    const track = root.querySelector('[data-itk-faq-nav] ul');
    let active = null;
    let ticking = false;

    /* ---------- search */
    const search = root.querySelector('[data-itk-faq-search]');
    const input = root.querySelector('[data-itk-faq-input]');
    const status = root.querySelector('[data-itk-faq-status]');
    const empty = root.querySelector('[data-itk-faq-empty]');
    const terms = root.querySelectorAll('[data-itk-faq-term]');
    let query = '';
    let timer = 0;

    function reset(clearInput) {
      clearTimeout(timer);
      query = '';
      if (clearInput && input) input.value = '';
      rows.forEach((row) => {
        row.el.hidden = false;
        row.el.open = row.open;
      });
      topics.forEach((topic) => {
        topic.el.hidden = false;
        if (topic.item) topic.item.hidden = false;
      });
      if (empty) empty.hidden = true;
      root.classList.remove('itk-faq-page--no-results');
      if (status) {
        status.textContent = '';
        status.classList.remove('itk-faq-page__status--quiet');
      }
      requestSpy();
    }

    function apply(raw) {
      const typed = String(raw || '').trim();
      const q = normalize(typed);
      if (q === query) return;
      if (!q) {
        reset(false);
        return;
      }
      query = q;
      const words = q.split(' ');
      let count = 0;
      rows.forEach((row) => {
        const match = words.every((word) => row.text.indexOf(word) !== -1);
        row.el.hidden = !match;
        if (match) {
          row.el.open = true;
          count += 1;
        }
      });
      topics.forEach((topic) => {
        const any = topic.rows.some((row) => !row.el.hidden);
        topic.el.hidden = !any;
        if (topic.item) topic.item.hidden = !any;
      });
      terms.forEach((term) => {
        term.textContent = typed;
      });
      if (empty) empty.hidden = count > 0;
      // phones: no topic left means an empty sticky chip row; CSS hides it below 990px
      root.classList.toggle('itk-faq-page--no-results', count === 0);
      if (status) {
        const template =
          count === 0 ? root.dataset.emptyText : count === 1 ? root.dataset.countOne : root.dataset.countMany;
        status.textContent = fill(template, count, typed);
        // the empty card already shows this sentence; keep it for screen readers only (its line stays reserved)
        status.classList.toggle('itk-faq-page__status--quiet', count === 0);
      }
      requestSpy();
    }

    if (search && input && rows.length) {
      search.hidden = false;
      input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => apply(input.value), DEBOUNCE_MS);
      });
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && input.value) {
          event.preventDefault();
          reset(true);
        }
      });
      // a value restored by the browser (back/forward cache, autofill) filters straight away
      if (input.value) apply(input.value);
    }

    /* ---------- scroll-spy */
    function setActive(topic) {
      if (topic === active) return;
      active = topic;
      topics.forEach((t) => {
        if (!t.link) return;
        if (t === topic) t.link.setAttribute('aria-current', 'true');
        else t.link.removeAttribute('aria-current');
      });
      // phones: keep the active chip inside the scrolling row (scrolls the row only, never the page or focus)
      if (!topic || !topic.link || !track || track.scrollWidth <= track.clientWidth + 1) return;
      const trackBox = track.getBoundingClientRect();
      const linkBox = topic.link.getBoundingClientRect();
      const inset = parseFloat(getComputedStyle(track).paddingLeft) || 16;
      if (linkBox.left < trackBox.left + inset || linkBox.right > trackBox.right - 40) {
        track.scrollTo({
          left: track.scrollLeft + (linkBox.left - trackBox.left) - inset,
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      }
    }

    function measure() {
      ticking = false;
      const visible = topics.filter((topic) => !topic.el.hidden);
      if (!visible.length) {
        setActive(null);
        return;
      }
      // the reading line sits just under the fixed header (and the sticky chip row on phones)
      let line = (header ? header.getBoundingClientRect().height : 68) + 24;
      if (chipQuery.matches && aside) line += aside.getBoundingClientRect().height;
      let current = visible[0];
      for (const topic of visible) {
        if (topic.el.getBoundingClientRect().top - line <= 1) current = topic;
        else break;
      }
      // a short last topic can't reach the line: mark it once the page is scrolled to the end
      const doc = document.documentElement;
      if (window.innerHeight + window.scrollY >= doc.scrollHeight - 4) {
        const last = visible[visible.length - 1];
        if (last.el.getBoundingClientRect().top < window.innerHeight) current = last;
      }
      setActive(current);
    }

    function requestSpy() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(measure);
    }

    if (topics.length) {
      window.addEventListener('scroll', requestSpy, { passive: true });
      window.addEventListener('resize', requestSpy, { passive: true });
      requestSpy();
    }

    root.itkFaq = {
      reset: () => reset(true),
      destroy() {
        clearTimeout(timer);
        root.classList.remove('itk-faq-page--no-results');
        window.removeEventListener('scroll', requestSpy);
        window.removeEventListener('resize', requestSpy);
        delete root.itkFaq;
      }
    };
  }

  /* ------------------------------------------------------------------ boot */

  function initAll(scope) {
    const pages = scope.matches && scope.matches('[data-itk-faq-page]') ? [scope] : [];
    scope.querySelectorAll('[data-itk-faq-page]').forEach((el) => pages.push(el));
    pages.forEach(initPage);
  }

  function boot() {
    initAll(document.documentElement);
    openFromHash();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  window.addEventListener('hashchange', openFromHash);

  // theme editor: re-bind a re-rendered FAQ page, drop listeners of a removed one
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-itk-faq-page]').forEach((el) => {
      if (el.itkFaq) el.itkFaq.destroy();
    });
  });
})();
